'use client';

import * as React from 'react';
import Image from 'next/image';
import { Globe, Check } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { routing, type Locale } from '@/i18n/routing';
import { useLocale } from 'next-intl';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const LOCALE_SET = new Set(routing.locales as readonly string[]);

function replaceLocaleInPathname(pathname: string, next: Locale) {
    if (pathname === '/' || pathname === '') return `/${next}`;
    const parts = pathname.split('/');
    if (LOCALE_SET.has(parts[1])) {
        parts[1] = next;
        return parts.join('/') || `/${next}`;
    }
    return `/${next}${pathname}`;
}

export function LangToggle() {
    const router = useRouter();
    const pathname = usePathname();
    const current = useLocale() as Locale;

    const switchLanguage = (next: Locale) => {
        if (next === current) return;

        document.cookie = `NEXT_LOCALE=${next}; path=/; max-age=${60 * 60 * 24 * 365}`;

        const target = replaceLocaleInPathname(pathname, next);
        router.replace(target);
        router.refresh();
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="cursor-pointer">
                    <Globe className="h-[1.2rem] w-[1.2rem]" />
                    <span className="sr-only">Switch language</span>
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
                {routing.locales.map((l) => (
                    <DropdownMenuItem
                        key={l}
                        onClick={() => switchLanguage(l)}
                        className="flex items-center gap-2 cursor-pointer"
                    >
                        {l === 'en'
                        ? <Image src="/images/flags/uk.svg" alt="English flag" width={20} height={15} className="rounded-full" />
                        : <Image src="/images/flags/fr.svg" alt="French flag" width={20} height={20} className="rounded-full" />
                      }
                        <span className="flex-1">{l === 'en' ? 'English' : 'Français'}</span>
                        {current === l && <Check className="h-4 w-4 opacity-60" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
